
/**
 * Module dependencies.
 */

import { color, units } from 'src/styles/utils';
import { ifProp, theme } from 'styled-tools';
import { useRouter } from 'next/router';
import React, { ReactElement } from 'react';
import RouterLink from 'src/components/core/links/router-link';
import Svg from 'src/components/core/svg';
import Type from 'src/components/core/typography';
import arrowLeftIcon from 'src/assets/svg/arrow-left.svg';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  className?: string,
  href?: string,
  icon?: string,
  isKiosk?: boolean,
  label: string,
  onClick?: () => void
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.button.attrs<Props>(({ href }) => {
  const element = href && RouterLink || 'button';

  return {
    as: element,
    type: element === 'button' ? 'button' : null
  };
})`
  align-items: center;
  appearance: none;
  background: none;
  border: 0;
  color: ${color('grey400')};
  cursor: pointer;
  display: flex;
  outline: none;
  transition: color ${theme('animations.defaultTransition')};

  &:focus,
  &:hover {
    color: ${color('brown400')};

    span {
      color: ${color('brown400')};
    }
  }
`;

/**
 * `StyledSvg` styled component.
 */

const StyledSvg = styled(Svg)`
  color: ${color('black')};
  transition: color ${theme('animations.defaultTransition')};
`;

/**
 * `Label` styled component.
 */

const Label = styled(Type.Paragraph)<{ isKiosk?: boolean }>`
  margin-left: ${units(2)};
  text-transform: uppercase;

  ${ifProp('isKiosk', css`
    font-size: 10.5px;
    letter-spacing: 0.75px;
    line-height: 18px;
  `)}
`;

/**
 * `Navigation` component.
 */

const Navigation = (props: Props): ReactElement => {
  const { className, href, icon, isKiosk, label, onClick } = props;
  const { locale } = useRouter();

  return (
    <Wrapper
      className={className}
      {...href ? { href, locale } : { onClick }}
    >
      <StyledSvg
        icon={icon ?? arrowLeftIcon}
        size={isKiosk ? '15px' : units(2.5)}
      />

      <Label isKiosk={isKiosk}>
        {label}
      </Label>
    </Wrapper>
  );
};

/**
 * Export `Navigation` component.
 */

export default Navigation;
